import cn from "classnames";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";
import { Label } from "reactstrap";
import { GENERATING_REPORT } from "../../constants/loading.constant";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { FacebookAnalyticsDateRangeFilter } from "../component/analytics/FacebookAnalyticsDateRangeFilter";
import { SummaryAnalyticsDashboard } from "../component/analytics/SummaryAnalyticsDashboard";
import { SummaryAnalyticsLoading } from "../component/analytics/SummaryAnalyticsLoading";
import { SummaryReportActions } from "../component/analytics/SummaryReportActions";
import { useSummaryAnalytics } from "../component/analytics/summary/useSummaryAnalytics";
import { UpdateLoadingCard } from "../component/UpdateLoadingCard";
import { LoadingSpinnerWembii } from "../component/LoadingSpinnerWembii";
import { PanelWembii } from "../component/PanelWembii";
import Content from "../layout/Content";
import Layout from "../layout/Layout";
import "../component/analytics/SummaryAnalytics.scss";

export const SummaryAnalytics = () => {
  const intl = useIntl();
  const { goToSocial } = useRouterCore();
  const {
    company,
    companyPlan,
    companyRole,
    isAdmin,
    isReseller,
  } = useMeCore();
  const { snapshot, loading, loadError, hasAnySync, syncedPlatforms, pendingPlatforms } =
    useSummaryAnalytics(true);

  const showInitialLoading = loading && !snapshot;
  const loadedPlatformCount = syncedPlatforms.length - pendingPlatforms.length;

  useEffect(() => {
    if (
      !companyPlan?.rules.allowAnalytics ||
      (!companyRole?.analitycs &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    ) {
      goToSocial();
    }
  }, [goToSocial, companyPlan, companyRole, isAdmin, isReseller]);

  return (
    <Layout>
      <Content>
        <div className="summary-analytics-page__header d-flex align-items-center justify-content-between flex-wrap mb-3">
          <div className="summary-analytics-page__titles">
            <Label className="summary-analytics-page__eyebrow mb-0 font-weight-bold text-uppercase small">
              {intl.formatMessage({ id: "app.sidebar.metrics" })}
            </Label>
            <h2 className="summary-analytics-page__title mb-0">
              {intl.formatMessage({ id: "app.analytics.summary.title" })}
            </h2>
          </div>
          <div className="summary-analytics-page__actions d-flex align-items-center flex-wrap gap-2">
            <FacebookAnalyticsDateRangeFilter />
            <SummaryReportActions disabled={!snapshot} />
          </div>
        </div>

        <UpdateLoadingCard
          loadingId={GENERATING_REPORT}
          text={intl.formatMessage({ id: "app.analytics.summary.generatingReport" })}
        />

        <PanelWembii
          className={cn("summary-analytics-page__panel", {
            "summary-analytics-page__panel--loading": showInitialLoading,
          })}
        >
          {showInitialLoading ? (
            <SummaryAnalyticsLoading platforms={syncedPlatforms} />
          ) : loadError ? (
            <div className="summary-analytics__empty text-danger">{loadError}</div>
          ) : snapshot ? (
            <>
              {pendingPlatforms.length > 0 ? (
                <div
                  className="summary-analytics-page__progress-banner"
                  aria-live="polite"
                >
                  <LoadingSpinnerWembii active size="sm" />
                  <span>
                    {intl.formatMessage(
                      { id: "app.analytics.summary.loadingProgress" },
                      {
                        current: loadedPlatformCount,
                        total: syncedPlatforms.length,
                      }
                    )}
                  </span>
                </div>
              ) : null}
              <SummaryAnalyticsDashboard
                snapshot={snapshot}
                companyName={company?.name}
              />
            </>
          ) : (
            <div className="summary-analytics__empty">
              {intl.formatMessage({
                id: hasAnySync
                  ? "app.analytics.summary.noData"
                  : "app.analytics.summary.noPlatforms",
              })}
            </div>
          )}
        </PanelWembii>
      </Content>
    </Layout>
  );
};
